import KioskBoard, { IKioskBoardOptions } from "kioskboard";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { AppState } from "redux/store";

const keyMap = [
    {
        "0": "Q",
        "1": "W",
        "2": "E",
        "3": "R",
        "4": "T",
        "5": "Z",
        "6": "U",
        "7": "I",
        "8": "O",
        "9": "P",
        "10": "Ü"
    },
    {
        "0": "A",
        "1": "S",
        "2": "D",
        "3": "F",
        "4": "G",
        "5": "H",
        "6": "J",
        "7": "K",
        "8": "L",
        "9": "Ö",
        "10": "Ä"
    },
    {
        "0": "Y",
        "1": "X",
        "2": "C",
        "3": "V",
        "4": "B",
        "5": "N",
        "6": "M"
    },
    {
        "0": "@",
        "1": ",",
        "2": ".",
        "3": "#",
        "4": "!",
        "5": "?",
        "6": "-"
    }
];

export const useVirtualKeyboard = (
    selector: string | HTMLInputElement | HTMLTextAreaElement | null,
    options?: IKioskBoardOptions
) => {
    useSelector((state: AppState) => state.api.isPending);
    const router = useRouter();
    const active = selector && router.pathname === "/kiosk/[key]";
    const keyboardRef = useRef<typeof KioskBoard>(undefined);
    const loadingRef = useRef(false);
    const observerRef = useRef<MutationObserver | undefined>(undefined);

    const settings = useMemo(
        () => ({
            allowMobileKeyboard: true,
            allowRealKeyboard: true,
            autoScroll: false,
            cssAnimations: false,
            keysArrayOfObjects: keyMap,
            keysEnterText: "Schließen",
            keysIconSize: "20px",
            keysFontSize: "16px",
            capsLockActive: false,
            ...options
        }),
        [selector, options]
    );

    // kioskboard binds a focus listener per element, so every input has to be
    // handed over once — and only once, otherwise the listeners stack up
    const attach = () => {
        const keyboard = keyboardRef.current;
        if (!keyboard) return;

        if (typeof selector !== "string") {
            keyboard.run(selector, settings);
            return;
        }

        window.document.querySelectorAll<HTMLInputElement>(selector).forEach((input) => {
            if (input.dataset.kioskboardBound) return;
            input.dataset.kioskboardBound = "true";
            keyboard.run(input, settings);
        });
    };

    // the observer below outlives this render, so it always calls the current attach
    const attachRef = useRef(attach);
    attachRef.current = attach;

    // inputs of field arrays (e.g. "Mitspieler hinzufügen") appear without this
    // component re-rendering, so watch the DOM instead of the render cycle
    const observeDom = () => {
        if (typeof selector !== "string" || observerRef.current !== undefined) return;
        observerRef.current = new MutationObserver(() => attachRef.current());
        observerRef.current.observe(window.document.body, { childList: true, subtree: true });
    };

    useEffect(() => {
        if (!active || !document) return;

        if (keyboardRef.current === undefined) {
            if (loadingRef.current) return;
            loadingRef.current = true;
            // import modded kioskboard
            import("lib/kioskboard").then((mod) => {
                keyboardRef.current = mod.default;
                console.debug("Keyboard Loaded!", keyboardRef.current);
                attachRef.current();
                observeDom();
                console.debug("Keyboard Cold Start!");
            });
            return;
        }

        attach();
        observeDom();
    });

    // stop observing once the view using the keyboard goes away
    useEffect(
        () => () => {
            observerRef.current?.disconnect();
            observerRef.current = undefined;
        },
        []
    );
};
